"use client";

import { useEffect, useRef } from "react";
import { useRouter } from "next/navigation";

/**
 * Quietly polls for an agent-triggered "refresh your dashboard" signal and, when a
 * newer one arrives, calls router.refresh() so the member's OPEN dashboard reflects
 * agent-side changes (e.g. a service added during a call) without them reloading
 * the page themselves.
 *
 * Renders nothing. Mounted once (in the dashboard layout) for real member sessions
 * only — not the read-only agent preview. Because /my-account pages are dynamic
 * Server Components fetched with `no-store`, router.refresh() re-runs the server
 * render into the same tab (preserving scroll / client state), no full reload.
 */
const POLL_MS = 12000;
const START_DELAY_MS = 3000;

export function LiveRefreshPoller() {
  const router = useRouter();
  const lastSeen = useRef<number | null>(null);
  const primed = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    async function poll() {
      try {
        // Trailing slash: the site uses trailingSlash:true, so hit the canonical
        // URL directly and avoid a 308 redirect hop on every poll.
        const res = await fetch("/api/member/refresh-signal/", { cache: "no-store" });
        if (res.ok) {
          const { refreshAt } = (await res.json()) as { refreshAt: number | null };
          if (!cancelled) {
            if (!primed.current) {
              // First SUCCESSFUL read only establishes a baseline — never refresh
              // on (re)load, and never for a signal that predates this session.
              lastSeen.current = typeof refreshAt === "number" ? refreshAt : null;
            } else if (
              typeof refreshAt === "number" &&
              (lastSeen.current === null || refreshAt > lastSeen.current)
            ) {
              lastSeen.current = refreshAt;
              router.refresh();
            }
            // Prime ONLY on a genuine read — a failed first poll must not let a
            // later read of a pre-session marker count as "new".
            primed.current = true;
          }
        }
      } catch {
        /* offline / transient — ignore and try again next tick (stay un-primed) */
      }
    }

    async function loop() {
      if (cancelled) return;
      await poll();
      if (!cancelled) timer = setTimeout(loop, POLL_MS);
    }

    timer = setTimeout(loop, START_DELAY_MS);

    // Extra one-off check when the tab regains focus (an agent may have pushed
    // while it was hidden). Does not start a second loop.
    const onVisible = () => {
      if (document.visibilityState === "visible" && primed.current) poll();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [router]);

  return null;
}
